import { captureAction, captureError } from '@smg-real-estate/browser-rum';
import { type HgSupportedLanguage } from '@smg-real-estate/domus';
import {
  GeoApiClient,
  type GeoLocation,
  type GeoLocationType,
  type GeoSearchResult,
  type GeoSearchResultItem,
  LayerClient,
  type Locale,
} from '@smg-real-estate/geo-libs';
import { type AxiosResponse } from 'axios';

const MAX_AUTOCOMPLETE_RESULT_COUNT = 40;

const GEO_API_ENDPOINT = import.meta.env.VUE_GEO_API_ENDPOINT;

let layerClient: LayerClient | null; // Serves server-side only
let geoApiClient: GeoApiClient | null;
const getLayerClient = (): LayerClient => (layerClient ??= new LayerClient());
const getGeoApiClient = (): GeoApiClient =>
  (geoApiClient ??= new GeoApiClient(GEO_API_ENDPOINT, {
    baseURL: GEO_API_ENDPOINT,
  }));

export const resetGeoApiClient = (): void => {
  geoApiClient = null;
};

export const resetLayerClient = (): void => {
  layerClient = null;
};

export const fetchGeoLocations = async (
  searchText: string,
  language: HgSupportedLanguage,
): Promise<GeoSearchResultItem[]> => {
  try {
    const geoLocationsSearchResult = await getGeoApiClient().findGeoLocations(
      language,
      searchText,
      MAX_AUTOCOMPLETE_RESULT_COUNT,
      undefined,
      undefined,
    );
    return geoLocationsSearchResult.results;
  } catch (e) {
    captureError(e);
    return [];
  }
};

// From geo layer first, if no data, fetch from geo api
export const getGeoLocationById = async (id: string): Promise<GeoLocation | null> => {
  try {
    let result = getLayerClient().getGeoLocationsById([id]);

    if (!result || !result.length) {
      result = await getGeoApiClient().getGeoLocationsById([id]);
    }
    return result?.[0] ?? null;
  } catch (err) {
    captureError(err);
    return null;
  }
};

// From geo layer first, if no data, fetch from geo api
export const getGeoLocationByUrlName = async (
  urlName: string,
  language: HgSupportedLanguage,
): Promise<GeoLocation | null> => {
  const locale = language as unknown as Locale;
  try {
    let result = getLayerClient().getGeoLocationByUrlName(urlName, locale);

    if (!result) {
      result = await getGeoApiClient().getGeoLocationByUrlName(urlName, locale);
    }
    return result ?? null;
  } catch (err) {
    captureError(err);
    return null;
  }
};

export const fetchGeoLocationsByIds = async (ids: string[]): Promise<GeoLocation[]> => {
  try {
    let result = getLayerClient().getGeoLocationsById(ids);
    if (!result?.length) {
      result = await getGeoApiClient().getGeoLocationsById(ids);
    }

    return result ?? [];
  } catch (e) {
    captureError(e);
    return [];
  }
};

export const getSurroundingLocationsByPolygon = async (
  geoId: string,
  types: GeoLocationType,
  polygon: string[][],
  size: number = MAX_AUTOCOMPLETE_RESULT_COUNT,
): Promise<GeoLocation[]> => {
  try {
    const response: AxiosResponse<GeoSearchResult> = await getGeoApiClient().axios.post(
      'locations',
      {
        size,
        types,
        polygon,
      },
    );

    if (response?.data) {
      return response.data.results
        .filter((item) => item.geoLocation.id !== geoId)
        .map((item) => item.geoLocation);
    }

    return [];
  } catch (ex) {
    captureError(ex);
    return [];
  }
};

export const getGeoLocationPolygons = async (id: string): Promise<string[][]> => {
  try {
    const result: AxiosResponse<{
      bounds: string[];
      polygons: string[][][];
    }> = await getGeoApiClient().axios.get(`polygon-sod/${id}/max-points:50,buffer:0.1`);
    return result?.data?.polygons[0] ?? [];
  } catch (ex) {
    captureError(ex);
    return [];
  }
};

export const getMunicipalitiesOfCanton = async (cantonId: string): Promise<GeoLocation[]> => {
  const canton = await getGeoLocationById(cantonId);
  if (!canton) {
    captureAction(`Canton ${cantonId} not found`, { cantonId });
    return [];
  }

  const locations = getLayerClient()?.geoData?.locations;
  const municipalities = locations?.filter(
    (location) => location.type === 'municipality' && location.parentPaths?.[0].includes(cantonId),
  );
  if (municipalities?.length) {
    return municipalities;
  }

  // Canton => Districts
  const { children: districtsGeoTags } = canton;
  const districts = await fetchGeoLocationsByIds(districtsGeoTags ?? []);

  // District => Municipalities
  const geoLocations = await Promise.all(
    districts.map((d) => fetchGeoLocationsByIds(d.children ?? [])),
  );

  return geoLocations?.flat();
};
